import React, { useState } from "react";
import { ACCOUNT_SETTINGS_ROUTES } from "@/utils/mapping";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { BurgerIcon } from "@/components/Icons/BurgerIcon";
import { CrossIcon } from "@/components/Icons/CrossIcon";
import { Badge } from "@/components/ui/badge";
import { useLocation } from "wouter";
import { useAuthActions, useAuthState } from "@/store/auth";
import { LogOutIcon } from "@/components/Icons/LogOutIcon";
import { LinkIcon } from "@/components/Icons/LinkIcon";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/utils/analytics";
import { AnalyticsEventType } from "@/constants/analytics";
import { CreditsField } from "../CreditsField/CreditsField";
import { Separator } from "@/components/ui/separator";
import { RequestCreditsModal } from "../RequestCreditsModal/RequestCreditsModal";
import { getIsCreditsEnabled } from "@/utils/credits";
import { PurchaseCreditsModal } from "../PurchaseCreditsModal/PurchaseCreditsModal";
import env from "@/env";
import { useExplorerOnlyMode } from "@/hooks/useExplorerOnlyMode";
import { DOCS_LINK } from "@/constants";
import { stringify } from "qs";
import { explorerPrefixedPath } from "@/utils";
import { resolveExplorerOnlyExternalUrl } from "@/utils/tool";
import { SupportModal } from "../SupportModal/SupportModal";
import { useMainStore } from "@/store/main";
import { UserNotificationsButton } from "@/components/Common/UserNotificationsButton/UserNotificationsButton";
import { UserDashboardMenuItems } from "@/components/Common/UserDashboardMenuItems/UserDashboardMenuItems";

type NavExternalLinkProps = {
  href: string;
  children: React.ReactNode;
  onClick?: () => void;
};

const NavExternalLink: React.FC<NavExternalLinkProps> = ({ href, children, onClick }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="subtext-small flex flex-row items-center gap-2 py-3 text-gray hover:text-foreground"
    onClick={onClick}
  >
    {children}
    <LinkIcon className="h-[13px]" />
  </a>
);

type NavInternalLinkProps = {
  href: string;
  children: React.ReactNode;
  onNavigate?: () => void;
};

const NavInternalLink: React.FC<NavInternalLinkProps> = ({ href, children, onNavigate }) => {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate();
    }
  };
  return (
    <a
      href={href}
      className="subtext-small flex flex-row items-center gap-2 py-3 text-gray hover:text-foreground"
      onClick={handleClick}
    >
      {children}
    </a>
  );
};

export const MobileNav: React.FC = () => {
  const [location, setLocation] = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const mobileSidebarSheetOpen = useMainStore((state) => state.mobileSidebarSheetOpen);
  const toggleMobileSidebarSheet = useMainStore((state) => state.toggleMobileSidebarSheet);
  const { logout } = useAuthActions();
  const isLoggedIn = useAuthState((state) => state.status === "authenticated");
  const [isRequestCreditsModalOpen, setIsRequestCreditsModalOpen] = useState(false);
  const [isPurchaseCreditsModalOpen, setIsPurchaseCreditsModalOpen] = useState(false);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const explorerOnlyMode = useExplorerOnlyMode();

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
  };

  return (
    <>
      <SupportModal
        isOpen={isSupportModalOpen}
        onClose={() => {
          setIsSupportModalOpen(false);
        }}
      />
      <PurchaseCreditsModal
        isOpen={isPurchaseCreditsModalOpen}
        onClose={() => {
          setIsPurchaseCreditsModalOpen(false);
        }}
      />
      <RequestCreditsModal
        isOpen={isRequestCreditsModalOpen}
        onClose={() => {
          setIsRequestCreditsModalOpen(false);
        }}
      />
      <div className="flex items-center gap-1">
        {/* When there's a sidebar to open (logged-in normal mode), the burger toggles it.
            When there's no sidebar (EXPLORER_ONLY_MODE or not logged in), the burger opens the Popover menu. */}
        {!explorerOnlyMode && isLoggedIn ? (
          <Button
            type="button"
            variant="outline"
            aria-label="Toggle navigation menu"
            onClick={() => toggleMobileSidebarSheet()}
            className={cn(
              "relative z-[3] flex h-9 w-9 cursor-pointer items-center rounded-[8px] border bg-gray-dark-1700 p-2.5",
              mobileSidebarSheetOpen ? "border-gray-dark-1200" : "border-gray-dark-200"
            )}
          >
            {mobileSidebarSheetOpen ? (
              <CrossIcon className="pointer-events-none h-4 w-4 text-gray-dark-1000" />
            ) : (
              <BurgerIcon className="h-4 w-4 text-gray-dark-1000" />
            )}
          </Button>
        ) : null}
        {/* Popover menu: only shown when there's no sidebar (EXPLORER_ONLY_MODE or not logged in) */}
        {(explorerOnlyMode || !isLoggedIn) && <Popover open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-label="Toggle navigation menu"
              className={cn(
                "relative z-[3] flex h-9 w-9 cursor-pointer items-center rounded-[8px] border bg-gray-dark-1700 p-2.5",
                menuOpen ? "border-gray-dark-1200" : "border-gray-dark-200"
              )}
            >
              {menuOpen ? (
                <CrossIcon className="pointer-events-none h-4 w-4 text-gray-dark-1000" />
              ) : (
                <BurgerIcon className="h-4 w-4 text-gray-dark-1000" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={8}
            className="z-[999999] w-[min(100vw-2rem,22rem)] border border-gray-dark-200 bg-gray-dark-1400 p-0 text-popover-foreground shadow-md"
          >
            <div className="flex w-full flex-col items-center justify-center px-6 py-3">
              {explorerOnlyMode && isLoggedIn && (
                <div className="flex w-full items-center justify-between py-2">
                  <span className="subtext-small text-gray">Notifications</span>
                  <UserNotificationsButton />
                </div>
              )}
              {getIsCreditsEnabled() && (
                <>
                  <CreditsField
                    onPurchaseCredits={() => {
                      if (env.FEATUREFLAG_PURCHASE_CREDITS_ENABLED) {
                        setIsPurchaseCreditsModalOpen(true);
                        return;
                      }

                      setIsRequestCreditsModalOpen(true);
                    }}
                  />
                  <Separator className="mt-5" />
                </>
              )}

              {ACCOUNT_SETTINGS_ROUTES.map(({ href, disabled, label, requiresLoggin, explorerOnlyPath }, index) => {
                if (!isLoggedIn && requiresLoggin) {
                  return null;
                }

                const path = explorerPrefixedPath(href);
                const isActive = location.includes(path);
                return (
                  <div
                    key={`mobile_nav_settings_route-${index}`}
                    onClick={() => {
                      if (explorerOnlyMode && explorerOnlyPath) {
                        window.open(
                          resolveExplorerOnlyExternalUrl(explorerOnlyPath),
                          "_blank",
                          "noopener,noreferrer"
                        );
                        setMenuOpen(false);
                        return;
                      }
                      setLocation(path);
                      setMenuOpen(false);
                    }}
                    className={cn(
                      `subtext-small relative w-full whitespace-nowrap border-b-[2px] border-gray-dark-1400 px-3 py-3 text-center
                        hover:text-foreground hover:before:absolute hover:before:bottom-[5%] hover:before:left-[40%] hover:before:right-[40%]
                        hover:before:top-[50%] hover:before:block hover:before:bg-gray-dark-200 hover:before:opacity-80 hover:before:blur-[12px]`,
                      isActive
                        ? `border-b-2 border-white text-foreground  before:absolute before:bottom-[5%] before:left-[40%]
                        before:right-[40%] before:top-[50%] before:block before:bg-gray-dark-200 before:opacity-85 before:blur-[12px]`
                        : "text-gray-dark-1000",
                      disabled
                        ? "pointer-events-none flex justify-center gap-4 dark:text-gray-dark-1200"
                        : "cursor-pointer"
                    )}
                  >
                    <span className="relative z-[1]">{label}</span>
                    {disabled && <Badge className="h-6 rounded-[6px] px-2">Coming Soon</Badge>}
                  </div>
                );
              })}

              {isLoggedIn && (
                <UserDashboardMenuItems
                  isMenuOpen={menuOpen}
                  variant="mobile"
                  onNavigate={() => setMenuOpen(false)}
                />
              )}

              <NavExternalLink
                href="https://support.io.net"
                onClick={() => {
                  trackEvent(AnalyticsEventType.SUPPORT_CLICKED);
                }}
              >
                Support Portal
              </NavExternalLink>

              <a
                className="subtext-small flex cursor-pointer flex-row items-center gap-2 py-3 text-gray"
                onClick={() => {
                  setIsSupportModalOpen(true);
                  setMenuOpen(false);
                }}
              >
                Open Support Ticket
              </a>
              <NavExternalLink href={DOCS_LINK}>Io Documentation</NavExternalLink>
              {isLoggedIn && (
                <NavInternalLink
                  href={explorerPrefixedPath("/id/funds")}
                  onNavigate={() => {
                    setLocation(explorerPrefixedPath("/id/funds"));
                    setMenuOpen(false);
                  }}
                >
                  Manage Funds
                </NavInternalLink>
              )}
              {isLoggedIn ? (
                <div
                  key="mobile_nav_route_logout"
                  onClick={handleLogout}
                  className={cn(
                    "subtext-small flex w-full cursor-pointer justify-center py-3 text-center text-red-light-800"
                  )}
                >
                  <LogOutIcon className="mr-1 h-[18px]" />
                  <span>Sign Out</span>
                </div>
              ) : (
                <>
                  <div
                    key="mobile_nav_route_logout"
                    onClick={() => {
                      setMenuOpen(false);
                      setLocation(
                        `${explorerPrefixedPath("/login/signup")}?${stringify({
                          r: location
                        })}`
                      );
                    }}
                    className={cn(
                      "subtext-small flex w-full cursor-pointer justify-center py-3 text-center text-foreground"
                    )}
                  >
                    <LogOutIcon className="mr-1 h-[18px]" />
                    <span>Sign Up</span>
                  </div>
                  <div
                    key="mobile_nav_route_logout"
                    onClick={() => {
                      setMenuOpen(false);
                      setLocation(
                        `${explorerPrefixedPath("/login")}?${stringify({
                          r: location
                        })}`
                      );
                    }}
                    className={cn(
                      "subtext-small flex w-full cursor-pointer justify-center py-3 text-center text-foreground"
                    )}
                  >
                    <LogOutIcon className="mr-1 h-[18px]" />
                    <span>Login</span>
                  </div>
                </>
              )}
            </div>
          </PopoverContent>
        </Popover>}
      </div>
    </>
  );
};
